import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { groupOrderHistory } from '../../utils/orderHistory';

const dateTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};

// The order and parcel log as milestones. One line per milestone; open one to
// read every event recorded under it. `limit` keeps a quick view short without
// hiding that more exists.
export default function OrderHistory({ timeline, limit = null, headingId = 'order-history-heading', title = 'History' }) {
  const groups = useMemo(() => groupOrderHistory(timeline), [timeline]);
  const shown = limit ? groups.slice(-limit) : groups;
  const hidden = groups.length - shown.length;
  const [open, setOpen] = useState(() => new Set());
  const toggle = (key) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="font-semibold">{title}</h3>
      {groups.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">No activity has been recorded for this order yet.</p>
      ) : (
        <>
          {hidden > 0 ? <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">Showing the latest {shown.length} of {groups.length} stages. Open the full record for the rest.</p> : null}
          <ol className="mt-3 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
            {shown.map((group) => {
              const expanded = open.has(group.key);
              const panelId = `${headingId}-${group.key}`;
              const many = group.events.length > 1;
              return (
                // `relative` keeps the row's screen-reader-only label inside the
                // row. Without a positioned ancestor that label is laid out
                // against the page, escapes the content scroller, and makes the
                // whole business frame scroll.
                <li key={group.key} className="relative">
                  <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => toggle(group.key)}
                    className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus dark:hover:bg-white/5">
                    <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${group.problem ? 'bg-warning-subtle' : 'bg-success-subtle'}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">{group.title}{group.problem ? <span className="sr-only"> (had a problem)</span> : null}</span>
                      <span className="block text-sm text-neutral-600 dark:text-neutral-300">{group.summary}{many ? ` · ${group.events.length} events` : ''}</span>
                    </span>
                    <span className="shrink-0 text-right text-xs tabular-nums text-neutral-600 dark:text-neutral-400">{dateTime(group.endedAt)}</span>
                    <ChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  {expanded ? (
                    <ol id={panelId} className="space-y-2 border-t border-neutral-200 bg-neutral-50 px-3 py-3 pl-8 dark:border-neutral-800 dark:bg-neutral-900/60">
                      {group.events.map((event, index) => (
                        <li key={`${event.at}-${index}`} className="text-sm">
                          <p className="flex flex-wrap items-baseline justify-between gap-x-3">
                            <span className={event.problem ? 'font-semibold text-warning' : 'font-medium'}>{event.label}</span>
                            <time className="text-xs tabular-nums text-neutral-600 dark:text-neutral-400">{dateTime(event.at)}</time>
                          </p>
                          {event.note ? <p className="mt-0.5 break-words text-neutral-600 dark:text-neutral-300">{event.note}</p> : null}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}
